import { PageHeader } from '@ant-design/pro-components';
import { Button, Checkbox, Col, Divider, Input, Row, Select, Table, Modal, Spin } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { CommunityPostResponseModel, CommunityPostSummaryResponseModel, CommunityTopicResponseModel } from '@uniquegood/realworld-admin-interface';
import React from 'react';
import { useAsyncFn } from 'react-use';
import axios from 'axios';
import { PlusOutlined } from '@ant-design/icons';
import DefaultLayout from '@src/components/DefaultLayout';
import { communityPostApi, communityTopicApi } from '@src/apis/admin';
import { Pagination } from '@src/components/Pagination';
import useModalState from '@src/hooks/useModalState';
import { columns } from './Columns';
import PostDetailModal from './Modal/PostDetailModal';
import CreatePostModal from './Modal/CreatePostModal';
import EditPostModal from './Modal/EditPostModal';

const PAGE_SIZE = 20;

export default function Post() {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedTopicId, setSelectedTopicId] = React.useState<string | undefined>(undefined);
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [withoutDeleted, setWithoutDeleted] = React.useState(false);
  const [selectedPost, setSelectedPost] = React.useState<CommunityPostResponseModel | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = React.useState(false);

  const { openModal: openDetailModal, closeModal: closeDetailModal, modal: detailModal } = useModalState();
  const { openModal: openCreateModal, closeModal: closeCreateModal, modal: createModal } = useModalState();
  const { openModal: openEditModal, closeModal: closeEditModal, modal: editModal } = useModalState();

  const [topicsState, fetchTopics] = useAsyncFn(async () => {
    const { data } = await communityTopicApi.getTopics();
    return data.data;
  }, []);

  const [postsState, fetchPosts] = useAsyncFn(async () => {
    const { data } = await communityPostApi.getCommunityPosts(
      selectedTopicId,
      searchKeyword || undefined,
      currentPage,
      PAGE_SIZE,
      withoutDeleted || undefined
    );
    return data.data;
  }, [selectedTopicId, currentPage, searchKeyword, withoutDeleted]);

  const totalCount = postsState.value?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  React.useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  React.useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedTopicId, searchKeyword, withoutDeleted]);

  const handleTopicChange = (value: string | undefined) => {
    setSelectedTopicId(value || undefined);
  };

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
  };

  const handleRowClick = async (record: CommunityPostSummaryResponseModel) => {
    setIsLoadingDetail(true);
    try {
      const { data } = await communityPostApi.getCommunityPostById(record.id);
      setSelectedPost(data.data ?? null);
      openDetailModal({});
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      const { data } = await communityPostApi.deletePost(postId);
      if (data.success) {
        Modal.success({
          title: '삭제되었습니다.',
          okText: '확인',
          onOk: () => {
            closeDetailModal();
            fetchPosts();
          },
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        const errorTitle = error.response?.data?.errorTitle;
        if (errorTitle === 'NotFoundCommunityPost') {
          Modal.error({
            title: '존재하지 않는 게시글입니다.',
            okText: '확인',
          });
          return;
        }
      }
      throw error;
    }
  };

  return (
    <>
      <DefaultLayout>
        <PageHeader
          title="커뮤니티 게시물 관리"
          subTitle="커뮤니티 게시물을 관리합니다."
          style={{ margin: '16px 20px 0px 20px' }}
        />
        <Divider />
        <Content style={{ padding: '0px 24px', margin: '0px 20px', marginBottom: '44px' }}>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col>
              <Select
                placeholder="토픽 선택"
                allowClear
                style={{ width: 200 }}
                onChange={handleTopicChange}
                loading={topicsState.loading}
                options={topicsState.value?.map((topic: CommunityTopicResponseModel) => ({
                  label: topic.name,
                  value: topic.id,
                }))}
              />
            </Col>
            <Col>
              <Input.Search
                placeholder="제목, 내용 검색"
                allowClear
                style={{ width: 300 }}
                onSearch={handleSearch}
                onChange={(e) => !e.target.value && setSearchKeyword('')}
              />
            </Col>
            <Col>
              <Checkbox
                checked={withoutDeleted}
                onChange={(e) => setWithoutDeleted(e.target.checked)}
              >
                삭제된 게시글 제외
              </Checkbox>
            </Col>
            <Col flex="auto" style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16 }}>
              <span style={{ color: '#666' }}>
                총 {totalCount}개의 게시물
              </span>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal({})}>
                게시글 작성
              </Button>
            </Col>
          </Row>

          <Spin spinning={isLoadingDetail} tip="게시글 로딩중...">
            <Table
              dataSource={postsState.value?.data ?? []}
              columns={columns}
              loading={postsState.loading}
              rowKey="id"
            pagination={false}
            sticky
            scroll={{ x: 1200 }}
            rowClassName={(record) => (record.deletedAt ? 'deleted-row' : '')}
            onRow={(record) => ({
              style: {
                cursor: 'pointer',
                ...(record.deletedAt && {
                  backgroundColor: '#fafafa',
                  color: '#bfbfbf',
                }),
              },
              onClick: () => handleRowClick(record),
            })}
            />
          </Spin>

          {totalPages > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </Content>
      </DefaultLayout>
      <PostDetailModal
        modalData={detailModal}
        post={selectedPost}
        onDelete={handleDeletePost}
        onEdit={() => {
          closeDetailModal();
          openEditModal({});
        }}
        onRecruitmentStatusChange={async () => {
          // 상태 변경 후 게시글 목록 갱신 및 상세 정보 갱신
          fetchPosts();
          if (selectedPost?.id) {
            const { data } = await communityPostApi.getCommunityPostById(selectedPost.id);
            setSelectedPost(data.data ?? null);
          }
        }}
      />
      <CreatePostModal
        modalData={createModal}
        closeModal={closeCreateModal}
        refetch={fetchPosts}
        topics={topicsState.value}
        topicsLoading={topicsState.loading}
      />
      <EditPostModal
        modalData={editModal}
        closeModal={closeEditModal}
        post={selectedPost}
        onSuccess={() => {
          fetchPosts();
        }}
      />
    </>
  );
}
